import * as React from 'react';
import { useServiceResolver } from '@services/useServiceResolver';
import { ULRpcSDKEffectService } from '@services/sdkEffect/ULRpcSDKEffectService';
import { getHeight, getWidth, debounce } from '@components/MutationEffect/utils';
import { setupMutationObserver } from '@components/MutationEffect/setupMutationObserver';
import { setupEventListeners } from '@components/MutationEffect/setupEventListeners';
import { usePrevious } from '@components/MutationEffect/hooks';

export const SizeMutationEffect: React.FC = props => {
    const { sdkEffect } = useServiceResolver();

    const [dimensions, setDimensions] = React.useState([0, 0]); // [height, width]
    const prevDimensions = usePrevious(dimensions);
    const elementRef = React.useRef<HTMLDivElement>(null);

    const sizeIFrame = () => {
        const currentHeight = getHeight();
        const currentWidth = getWidth();
        setDimensions([currentHeight, currentWidth]);
    };

    const sizeIFrameThrottled = debounce(sizeIFrame, 20);

    /**
     * Create event listeners and mutation observer to monitor changes in body
     * https://github.com/davidjbradshaw/iframe-resizer/blob/master/src/iframeResizer.contentWindow.js
     */
    React.useEffect(() => {
        if (!elementRef.current) {
            return;
        }

        sizeIFrame();
        const listenerCleanup = setupEventListeners(sizeIFrameThrottled);
        const observerCleanup = setupMutationObserver(sizeIFrameThrottled);

        return () => {
            listenerCleanup();
            observerCleanup();
        };
    }, [elementRef.current]);

    /**
     * when height or width changed
     * call sdkEffect to change window size
     */
    React.useEffect(() => {
        if (!!elementRef.current && sdkEffect instanceof ULRpcSDKEffectService) {
            const [height, width] = dimensions;
            const [prevHeight, prevWidth] = prevDimensions ?? [0, 0];
            if (height === prevHeight && width === prevWidth) {
                return;
            }
            sdkEffect.onWindowSizeChangedEffect(height, width);
        }
    }, [dimensions]);

    React.useEffect(() => {
        // stopInfiniteResizingOfIFrame
        document.documentElement.style.height = '';
        document.body.style.height = '';
    }, []);

    /**
     * to prevent re-render children, when state changed
     * because it doesn't relate to children.
     */
    const memoizedChildren = React.useMemo(() => props.children, [props.children]);

    return (
        <div ref={elementRef} data-cy="mutation-sensor">
            {memoizedChildren}
        </div>
    );
};
SizeMutationEffect.displayName = 'SizeMutationEffect';
